/**
 * Client Command Repository
 *
 * Handles all write operations for clients (create, update, delete).
 * This repository is responsible for modifying client data in the database.
 */

import { BaseRepository, RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Client } from '@/shared/types';
import { mapDatabaseClientToClient, mapClientToDatabaseClient } from '@/shared/services/dataMapper';

/**
 * Database Client entity
 */
interface DatabaseClient {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  company?: string;
  tax_id?: string;
  logo_url?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
  is_deleted?: boolean;
  edit_count?: number;
  deleted_at?: string;
}

/**
 * Client Command Repository
 * Provides write operations for the clients table
 */
export class ClientCommandRepository extends BaseRepository<Client> {
  protected tableName = 'clients' as const;

  /**
   * Create a new client with security checks
   * - Prevents re-adding recently deleted clients (30-day cooldown)
   * - Checks for duplicate email/name to prevent circumvention
   */
  async create(userId: string, clientData: Omit<Client, 'id' | 'userId'>): Promise<RepositoryResult<Client>> {
    try {
      const db = getDbClient();

      // Security check 1: Prevent re-adding recently deleted clients (30-day cooldown)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const { data: recentlyDeleted } = await db
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .eq('email', clientData.email)
        .eq('is_deleted', true)
        .gte('deleted_at', thirtyDaysAgo)
        .maybeSingle();

      if (recentlyDeleted) {
        return {
          success: false,
          error: `Cannot re-add this client. A client with this email was deleted on ${new Date(recentlyDeleted.deleted_at || '').toLocaleDateString()}. Please wait 30 days before re-adding or contact support.`,
        };
      }

      // Security check 2: Check for similar name to prevent circumvention by slightly changing names
      const { data: similarClients } = await db
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .eq('is_deleted', false)
        .ilike('name', `%${clientData.name}%`)
        .limit(3);

      if (similarClients && similarClients.length > 0) {
        const similarNames = similarClients.map((c: any) => c.name).join(', ');
        return {
          success: false,
          error: `A client with a similar name already exists: ${similarNames}. Please edit the existing client instead.`,
        };
      }

      // Security check 3: Initialize edit tracking fields
      const dbData = {
        user_id: userId,
        ...mapClientToDatabaseClient(clientData),
        edit_count: 0,
        created_at: new Date().toISOString(),
      };

      const { data, error } = await db
        .from(this.tableName)
        .insert(dbData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const client = mapDatabaseClientToClient(data as DatabaseClient);

      return {
        success: true,
        data: client,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'creating client'),
      };
    }
  }

  /**
   * Update an existing client
   * Handles edit_count tracking if provided
   */
  async update(id: string, clientData: Partial<Client>): Promise<RepositoryResult<Client>> {
    try {
      const db = getDbClient();

      const dbData = {
        ...mapClientToDatabaseClient(clientData),
        updated_at: new Date().toISOString(),
      } as Partial<DatabaseClient> & { updated_at: string };

      // Handle edit_count if provided (it should be incremented by the caller)
      if ('editCount' in clientData && clientData.editCount !== undefined) {
        (dbData as any).edit_count = clientData.editCount;
      }

      const { data, error } = await db
        .from(this.tableName)
        .update(dbData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Client not found',
        };
      }

      const client = mapDatabaseClientToClient(data as DatabaseClient);

      return {
        success: true,
        data: client,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'updating client'),
      };
    }
  }

  /**
   * Soft delete a client (sets is_deleted flag)
   * Tracks deletion timestamp for security purposes
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'deleting client'),
      };
    }
  }

  /**
   * Permanently delete a client (hard delete)
   * Use with caution - this cannot be undone
   */
  async hardDelete(id: string): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'permanently deleting client'),
      };
    }
  }

  /**
   * Restore a soft-deleted client
   */
  async restore(id: string): Promise<RepositoryResult<Client>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .update({ is_deleted: false, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Client not found',
        };
      }

      const client = mapDatabaseClientToClient(data as DatabaseClient);

      return {
        success: true,
        data: client,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'restoring client'),
      };
    }
  }

  /**
   * Bulk delete clients
   */
  async bulkDelete(ids: string[]): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .update({ is_deleted: true, updated_at: new Date().toISOString() })
        .in('id', ids);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'bulk deleting clients'),
      };
    }
  }

  /**
   * Update client logo
   */
  async updateLogo(id: string, logoUrl: string): Promise<RepositoryResult<Client>> {
    return this.update(id, { logoUrl });
  }

  /**
   * Update client contact information
   */
  async updateContact(
    id: string,
    contactData: { email?: string; phone?: string; address?: string }
  ): Promise<RepositoryResult<Client>> {
    return this.update(id, contactData);
  }

  /**
   * Merge duplicate clients
   * Combines data from two clients and keeps one
   */
  async mergeClients(
    primaryClientId: string,
    secondaryClientId: string
  ): Promise<RepositoryResult<Client>> {
    try {
      // Import dynamically to avoid circular dependency
      const { clientQueryRepository } = await import('./ClientQueryRepository');

      // Get both clients
      const primaryResult = await clientQueryRepository.findById(primaryClientId);
      const secondaryResult = await clientQueryRepository.findById(secondaryClientId);

      if (!primaryResult.success || !primaryResult.data) {
        return {
          success: false,
          error: 'Primary client not found',
        };
      }

      if (!secondaryResult.success || !secondaryResult.data) {
        return {
          success: false,
          error: 'Secondary client not found',
        };
      }

      const primary = primaryResult.data;
      const secondary = secondaryResult.data;

      // Merge data (keep most recent/complete information)
      const mergedData: Partial<Client> = {
        email: primary.email || secondary.email,
        phone: primary.phone || secondary.phone,
        address: primary.address || secondary.address,
        company: primary.company || secondary.company,
        logoUrl: primary.logoUrl || secondary.logoUrl,
        taxId: primary.taxId || secondary.taxId,
        notes: [primary.notes, secondary.notes].filter(Boolean).join('\n\n--- Merged ---\n\n'),
      };

      // Update primary client with merged data
      const updateResult = await this.update(primaryClientId, mergedData);

      if (updateResult.success) {
        // Delete secondary client
        await this.delete(secondaryClientId);
      }

      return updateResult;
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'merging clients'),
      };
    }
  }
}

// Export singleton instance
export const clientCommandRepository = new ClientCommandRepository();
